/**
 * Browser Geolocation for Product Tracking
 * Integrates with Iubenda cookie consent via Google Tag Manager
 */

class ProductGeolocation {
    constructor() {
        this.location = null;
        this.locationKey = 'userLocation';
        this.locationExpiry = 'userLocationExpiry';
        this.expiryHours = 24; // Cache location for 24 hours
        this.consentGiven = false;
    }

    /**
     * Enable geolocation (called when user accepts cookies)
     * Call this from Google Tag Manager when Iubenda consent is given
     */
    enableGeolocation() {
        this.consentGiven = true;
        this.init().catch(() => {
            // Silently fail - will use IP-based location as fallback
        });
    }

    /**
     * Disable geolocation (called when user rejects cookies)
     */
    disableGeolocation() {
        this.consentGiven = false;
        this.clearLocation();
    }

    /**
     * Initialize and get location (only if consent given)
     */
    async init() {
        if (!this.consentGiven) {
            return null;
        }

        // Check if we have a cached location
        const cached = this.getCachedLocation();
        if (cached) {
            this.location = cached;
            return cached;
        }

        // Try to get browser location
        if ('geolocation' in navigator) {
            try {
                this.location = await this.getBrowserLocation();
                if (this.location) {
                    this.cacheLocation(this.location);
                    return this.location;
                }
            } catch (error) {
                // Silently fail - will use IP-based location as fallback
            }
        }

        return null;
    }

    /**
     * Get cached location from localStorage
     */
    getCachedLocation() {
        try {
            const expiry = localStorage.getItem(this.locationExpiry);
            const location = localStorage.getItem(this.locationKey);

            if (!expiry || !location) {
                return null;
            }

            // Check if expired
            if (Date.now() > parseInt(expiry, 10)) {
                this.clearLocation();
                return null;
            }

            return location;
        } catch (e) {
            return null;
        }
    }

    /**
     * Cache location in localStorage
     */
    cacheLocation(location) {
        try {
            const expiry = Date.now() + (this.expiryHours * 60 * 60 * 1000);
            localStorage.setItem(this.locationKey, location);
            localStorage.setItem(this.locationExpiry, expiry.toString());
        } catch (e) {
            // localStorage not available, continue without caching
        }
    }

    /**
     * Clear cached location
     */
    clearLocation() {
        try {
            localStorage.removeItem(this.locationKey);
            localStorage.removeItem(this.locationExpiry);
            this.location = null;
        } catch (e) {
            // Ignore errors
        }
    }

    /**
     * Get browser location using Geolocation API
     */
    async getBrowserLocation() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;

                    // Reverse geocode to get city name
                    const location = await this.reverseGeocode(latitude, longitude);
                    resolve(location);
                },
                (error) => {
                    // User denied or error occurred
                    reject(error);
                },
                {
                    enableHighAccuracy: false, // We only need city-level, not GPS
                    timeout: 5000,
                    maximumAge: 3600000 // Accept cached position up to 1 hour old
                }
            );
        });
    }

    /**
     * Reverse geocode coordinates to city/region
     */
    async reverseGeocode(lat, lon) {
        try {
            const response = await fetch('/actions/appmodule/location/reverse-geocode', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ lat, lon })
            });

            const data = await response.json();

            if (data.success && data.location) {
                return data.location;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get location for product tracking
     * Returns null if no consent - will use IP-based location as fallback
     */
    async getLocationForTracking() {
        if (!this.consentGiven) {
            return null;
        }

        if (this.location) {
            return this.location;
        }

        return await this.init();
    }
}

// Initialize and expose globally
window.productGeolocation = new ProductGeolocation();

/**
 * INTEGRATION WITH IUBENDA VIA GOOGLE TAG MANAGER
 *
 * In Google Tag Manager, create a Custom HTML tag:
 *
 * TAG TYPE: Custom HTML
 * TRIGGER: Fire when Iubenda consent is given (when user accepts cookies)
 *
 * TAG CODE:
 * <script>
 *   window.productGeolocation.enableGeolocation();
 * </script>
 *
 * That's it! No need for a rejection tag - the system defaults to "no consent"
 * and will only enable geolocation when you explicitly call enableGeolocation().
 *
 * IMPORTANT: Make sure the trigger fires:
 * - When user accepts cookies for the first time
 * - When user comes back and cookies are already accepted
 * - Every page load where consent is active
 *
 * IUBENDA CALLBACK METHOD (Alternative):
 * Add this to your Iubenda configuration script:
 *
 * var _iub = _iub || [];
 * _iub.csConfiguration = {
 *     ...(your existing config),
 *     callback: {
 *         onPreferenceExpressedOrNotNeeded: function(preference) {
 *             if (!preference) {
 *                 preference = _iub.cs.consent;
 *             }
 *             // Only enable if user has given consent
 *             if (preference && preference.consent === true) {
 *                 window.productGeolocation.enableGeolocation();
 *             }
 *             // No need to call disableGeolocation() - it's disabled by default
 *         }
 *     }
 * };
 */
